import { Picture, Section, useViewport } from '@farmersdog/corgi-x';
import { getLinkToFreshSignup } from '@farmersdog/tosa/src/utils/getLinkToSignupFlow';
import { PATH_SIGNUP_ME } from '@farmersdog/constants/paths';
import { ExtendedText } from '../../../HomepageRedesign/components/ExtendedText';
import { ReviewsRedesignSections } from '../../../HomepageRedesign/components/CtaTrackerV2';
import { useTrackedSectionRef } from '../../../../hooks/useTrackedSections';

import { ExtendedButton } from '../../../HomepageRedesign/components';
import config from '../../../../config';
import { useCoupon, DiscountType } from '@farmersdog/coupons';

import TrustedByProsDesktopJpg from './assets/tbp-desktop.jpg';
import TrustedByProsDesktopWebp from './assets/tbp-desktop.webp';
import TrustedByProsMobileJpg from './assets/tbp-mobile.jpg';
import TrustedByProsMobileWebp from './assets/tbp-mobile.webp';

import DogOnLeash from './assets/dog-on-leash.svg';
import House from './assets/house.svg';
import Stethoscope from './assets/stethoscope.svg';
import { ApopLogo } from '../../../HomepageRedesign/assets/ApopLogo';
import { WsavaLogo } from '../../../HomepageRedesign/assets/WsavaLogo';

import styles from './TrustedByPros.module.css';

const SECTIONS = [
  {
    icon: <Stethoscope width={48} aria-hidden={true} />,
    title: 'Veterinary Professionals',
    content:
      '“I very much approach health recommendations with a ‘food is medicine’ philosophy and The Farmer’s Dog solved the challenges I was experiencing with home cooked options. It’s made the process so much more convenient than home cooking!”',
    author: 'Dr. Sarah Callahan, DVM',
  },
  {
    icon: <House width={48} aria-hidden={true} />,
    title: 'Shelters & Rescues',
    content:
      '“Dogs go CRAZY for the fresh dog food from The Farmer’s Dog we are so grateful for our ongoing partnership and the donations to ensure the pups in our care are eating healthy.”',
    author: 'Rescue City',
  },
  {
    icon: <DogOnLeash width={48} aria-hidden={true} />,
    title: 'Dog Professionals',
    content:
      '“Having picked up over 10,000 dog poops in my career, I know a thing or two about doo-doo. When your dog eats a nutritional meal like The Farmer’s Dog, their poops will speak for themselves. You should see my dog Ruby’s poops, they’re incredible.”',
    author: 'Dan’s Pet Care',
  },
];

interface TrustedByProsSection {
  icon: JSX.Element;
  title: string;
  content: string;
  author: string;
  isMobile: boolean;
}

const TrustedByProsSection = ({
  icon,
  title,
  content,
  author,
  isMobile,
}: TrustedByProsSection) => {
  return (
    <div className={styles.sectionContainer}>
      <div className={styles.iconContainer}>{icon}</div>
      <div>
        <ExtendedText
          variant={isMobile ? 'heading-16' : 'heading-22'}
          color="kale-3"
          as="div"
          bottomSpacing="sm"
          bold
        >
          {title}
        </ExtendedText>
        <ExtendedText
          as="div"
          variant="article-16"
          color="kale-3"
          bottomSpacing="sm"
        >
          {content}
        </ExtendedText>
        <ExtendedText
          as="div"
          color="kale-3"
          variant="heading-15"
          weight="medium"
        >
          {`- ${author}`}
        </ExtendedText>
      </div>
    </div>
  );
};

export function TrustedByPros() {
  const viewport = useViewport();
  const isMobile = !viewport?.lg;

  const sectionRef = useTrackedSectionRef(
    ReviewsRedesignSections.TrustedByPros
  );

  const linkToFreshSignup = getLinkToFreshSignup(PATH_SIGNUP_ME);

  const imageSources = isMobile
    ? [TrustedByProsMobileJpg, TrustedByProsMobileWebp]
    : [TrustedByProsDesktopJpg, TrustedByProsDesktopWebp];

  const { url, referrerRoute } = config('tosa.app.coreApi');
  const { discountAmount } = useCoupon({
    apiUrl: `${url}${referrerRoute}`,
    defaultDiscount: {
      discountType: DiscountType.percentage,
      discountAmount: config('features.defaultTrialDiscount'),
    },
  });

  const spacing = isMobile ? 'sm' : 'xl';

  return (
    <Section
      aria-label="Trusted by pros"
      vSpacing={spacing}
      sectionRef={sectionRef}
    >
      <div className={styles.container}>
        <Picture
          alt="Vets with dog"
          sources={imageSources}
          className={styles.image}
        />
        <div className={styles.textContainer}>
          <ExtendedText
            variant={isMobile ? 'heading-28' : 'heading-36'}
            color="kale-3"
            bold
          >
            Trusted by pros
          </ExtendedText>
          {SECTIONS.map((section, index) => (
            <TrustedByProsSection
              key={index}
              {...section}
              isMobile={isMobile}
            />
          ))}
          <ExtendedButton
            variant="solid-kale-2"
            className={styles.button}
            type="link"
            to={linkToFreshSignup}
          >
            Get {discountAmount}% Off Fresh Food
          </ExtendedButton>
          <div className={styles.logosContainer}>
            <WsavaLogo />
            <ApopLogo />
          </div>
        </div>
      </div>
    </Section>
  );
}
